import Cookie from "js-cookie";
import { ValidateEmail, LoginCredentials } from "@/api/forms";
import { api } from "../api";
import { backendErrors } from "./common";
import { LoginWithPhoneCredentials, SocialRegisterTypes } from "@/api/uas";
import { initializeApp } from "firebase/app";
import {
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
  getAuth
} from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyBT8XQlsB-v1VrACaQgYeVdj_mhjqNGiTs",
  authDomain: "am-sign-up---login.firebaseapp.com",
  databaseURL: "https://am-sign-up---login.firebaseio.com/",
  projectId: "am-sign-up---login",
  storageBucket: "am-sign-up---login.appspot.com",
  messagingSenderId: "551061712868",
  appId: "1:551061712868:web:e9350ae6ab719351aee1f1"
};

const firebaseApp = initializeApp(firebaseConfig);

export const ERROR_OTP_REQUIRED = "authentication_failed_otp_required";
export const ERROR_EMAIL_NOT_VERIFIED = "email_not_verified";
export const ERROR_INVALID_CREDENTIAL = "invalid_credentials";
export const ERROR_AUTHENTICATION_FAILED = "authentication_failed";
export const ERROR_USER_NOT_FOUND = "user_not_found";
export const ERROR_SOCIAL_PROVIDER_FAIL = "social_provider_fail";
export const ERROR_CLIENT_TERMNATION_PENDING = "client_termination_pending";
export const ERROR_NO_ACTIVE_PROFILES = "no_active_profiles";

export const companyAlias: Record<string, number> = {
  amas: 2,
  amuk: 12,
  ampty: 9,
  amcy: 13,
  amsey: 15,
  amjrd: 16,
  amsar: 17
};

export const LOGIN_STEPS = {
  LOGIN_STEP: "LOGIN_STEP",
  RESTORE_STEP: "RESTORE_STEP",
  CONFIRM_STEP: "CONFIRM_STEP",
  VERIFY_STEP: "VERIFY_STEP",
  REDIRECT_STEP: "REDIRECT_STEP",
  ONE_TIME_PASSWORD_STEP: "ONE_TIME_PASSWORD_STEP",
  RESTORE_BY_PHONE_STEP: "RESTORE_BY_PHONE_STEP",
  RESTORE_BY_PHONE_CONFIRMATION_STEP: "RESTORE_BY_PHONE_CONFIRMATION_STEP",
  THANK_YOU_STEP: "THANK_YOU_STEP"
};

export const SIGNUP_STEPS = {
  FIRST_STEP: "FIRST_STEP",
  SECOND_STEP: "SECOND_STEP",
  VERIFY_STEP: "VERIFY_STEP",
  LOGIN_STEP: "LOGIN_STEP",
  RESTORE_STEP: "RESTORE_STEP",
  RESTORE_BY_PHONE_STEP: "RESTORE_STEP",
  CONFIRM_STEP: "CONFIRM_STEP",
  THANK_YOU_STEP: "THANK_YOU_STEP",
  ONE_TIME_PASSWORD_STEP: "ONE_TIME_PASSWORD_STEP",
  SIGN_UP_STEP: "SIGN_UP_STEP"
};

export const SIGNUP_PHONE_STEPS = {
  FIRST_STEP: "FIRST_STEP",
  SECOND_STEP: "SECOND_STEP",
  SUCCESS_STEP: "SUCCESS_STEP",
  COUNTRY_SELECT_STEP: "COUNTRY",
  PASSWORD_STEP: "PASSWORD",
  OTP_STEP: "OTP_STEP"
};

export const SIGNUP_FLOW = {
  EMAIL: "EMAIL",
  PHONE: "PHONE"
};

export const ACTIVATE_LEAD_STEPS = {
  FIRST_STEP: "FIRST_STEP",
  THANK_YOU_STEP: "THANK_YOU_STEP"
};

export const RESET_PASSWORD_STEPS = {
  FIRST_STEP: "FIRST_STEP",
  REDIRECT_STEP: "REDIRECT_STEP",
  CODE_CONFIRM_STEP: "CODE_CONFIRM_STEP"
};

export const USER_STATUS = {
  USER_VERIFIED: "verified",
  USER_UNVERIFIED: "unverified",
  USER_NOTEXISTS: "notexists"
};

export const verifyEmail = async (hash: string) => {
  const { data } = await api.tr3.verifyCode(hash);

  return data;
};

export type LoginSubmitForm = (
  formData: LoginCredentials
) => Promise<{ next: string; data: any }>;

export type LoginByPhoneSubmitForm = (
  formData: LoginWithPhoneCredentials
) => Promise<{ next: string; data: any }>;

const handleLoginErrors = (response: any) => {
  const { errors, data } = response && response.data;

  if (errors?.code === ERROR_EMAIL_NOT_VERIFIED) {
    return {
      next: LOGIN_STEPS.VERIFY_STEP,
      data
    };
  }

  if (errors?.code === ERROR_OTP_REQUIRED) {
    return {
      next: LOGIN_STEPS.ONE_TIME_PASSWORD_STEP,
      data
    };
  }

  const throttleDelay =
    data && data.retryAfter ? parseInt(data.retryAfter) : null;

  throw { errors, data, throttleDelay };
};

export const loginFlow: LoginSubmitForm = async formData => {
  try {
    const {
      data: { data }
    } = await api.uas.login(formData);

    return { next: LOGIN_STEPS.REDIRECT_STEP, data };
  } catch ({ response }) {
    return handleLoginErrors(response);
  }
};

export const loginbyPhoneFlow: LoginByPhoneSubmitForm = async formData => {
  try {
    const {
      data: { data }
    } = await api.uas.loginbyPhone(formData);

    return { next: LOGIN_STEPS.REDIRECT_STEP, data };
  } catch ({ response }) {
    return handleLoginErrors(response);
  }
};

export const verifySms = async (code: string) => {
  try {
    await api.tr3.verifySms(code);

    return true;
  } catch (e) {
    return false;
  }
};

export const AUTH_METHODS = {
  EMAIL: "EMAIL",
  PHONE: "PHONE"
};

export const SOCIAL_PROVIDERS = {
  google: GoogleAuthProvider,
  facebook: FacebookAuthProvider
};

export const defaultSubmitFlow = async (formData: {
  entity: string;
  entity_type: SocialRegisterTypes;
}) => {
  try {
    const { data } = await api.uas.loginBySocial(formData);
    return { next: LOGIN_STEPS.REDIRECT_STEP, data: data.data };
  } catch ({ response }) {
    const data = handleLoginErrors(response);
    data.data.entity = formData.entity;
    return data;
  }
};

export const oAuthWithProvider = async (entity_type: SocialRegisterTypes) => {
  const authProvider = SOCIAL_PROVIDERS[entity_type];
  const provider = new authProvider();
  provider.addScope("email");
  const auth = getAuth(firebaseApp);

  return await signInWithPopup(auth, provider)
    .then(result => {
      const profile = getAdditionalUserInfo(result);
      const credential = authProvider.credentialFromResult(result);

      if (!credential || !credential.accessToken) {
        throw { code: "wrong_response" };
      }

      return {
        entity: credential.accessToken,
        entity_type,
        userInfo: profile?.profile
      };
    })
    .catch((error: any) => {
      throw { ...error, code: "social_provider_fail" };
    });
};

const loginMethods: Record<string, any> = {
  EMAIL: loginFlow,
  PHONE: loginbyPhoneFlow
};

export const getLoginFlow = (methodName: string) => {
  const methods: Record<string, any> = {
    ...loginMethods,
    GOOGLE: defaultSubmitFlow,
    FACEBOOK: defaultSubmitFlow
  };

  return methods[methodName] || null;
};

export const getLoginOptions = (translations: Record<string, string>) =>
  Object.keys(loginMethods).map((key: string) => ({
    label: translations[key.toLocaleLowerCase()] || "no translation",
    value: key
  }));

export const signUp = async (data: any) => {
  const { data: validateDate } = await api.forms.validateEmail(data);

  if (validateDate.errors) {
    throw { errors: backendErrors(validateDate.errors) };
  }

  if (!validateDate.success) {
    const { data: signupData } = await api.forms.signup(data);

    if (!signupData.success) {
      throw { errors: backendErrors(signupData.errors || {}) };
    }

    return {
      next: SIGNUP_STEPS.THANK_YOU_STEP,
      data: signupData
    };
  }

  if (!validateDate.is_verified_email) {
    throw {
      code: "email_exists"
    };
  }

  if (validateDate.is_verified_email) {
    throw {
      code: "email_exists"
    };
  }

  return {
    next: SIGNUP_STEPS.SIGN_UP_STEP,
    data: validateDate
  };
};

export const checkEmail = async (data: ValidateEmail) => {
  const { data: validateDate } = await api.forms.validateEmail(data);

  if (validateDate.errors) {
    throw { errors: backendErrors(validateDate.errors) };
  }

  if (validateDate.success) {
    throw { code: "email_exists" };
  }

  return {
    data: validateDate
  };
};

export const signUpFirstStep = async (data: ValidateEmail) => {
  const { data: validateDate } = await api.forms.validateEmail(data);

  if (validateDate.errors) {
    throw { errors: backendErrors(validateDate.errors) };
  }

  if (!validateDate.success) {
    return {
      next: SIGNUP_STEPS.SECOND_STEP,
      data: validateDate
    };
  }

  throw { code: "email_exists" };
};

/* Do not use this function anymore in signup forms. Instead, call api.forms.signupNew(data) to skip UAS. */
/* When each form is updated, this function should be removed at all */
export const signUpSecondStep = async (data: any) => {
  const { data: signupData } = await api.forms.signup(data);

  if (!signupData.success) {
    throw { errors: backendErrors(signupData.errors || {}) };
  }

  return {
    next: SIGNUP_STEPS.THANK_YOU_STEP,
    data: signupData
  };
};

export const activateLead = async (data: any) => {
  const { data: leadData } = await api.forms.activateLead(data);

  if (leadData.errors) {
    throw { errors: backendErrors(leadData.errors) };
  }

  return {
    next: ACTIVATE_LEAD_STEPS.THANK_YOU_STEP,
    data: leadData
  };
};

export const resetPassword = async (data: {
  password: string;
  password_confirm: string;
  hash: string;
}) => {
  try {
    await api.tr3.passwordReset(data);
  } catch (e) {
    throw { errors: backendErrors(e.response.data.fields) };
  }

  return {
    next: RESET_PASSWORD_STEPS.CODE_CONFIRM_STEP
  };
};

export const resetPasswordByPhone = async (data: {
  phone_number: string;
  voice_call?: boolean;
}) => {
  try {
    await api.uas.resetPasswordByPhoneRequest(data);
  } catch ({ response }) {
    throw { errors: response.data.errors, data: response.data.data };
  }
  return {
    next: RESET_PASSWORD_STEPS.CODE_CONFIRM_STEP
  };
};

export const resetPasswordByPhoneProceed = async (data: {
  phone_number: string;
  code: string;
}) => {
  try {
    const { data: response } = await api.uas.resetPasswordByPhoneProceed(data);
    return {
      next: RESET_PASSWORD_STEPS.REDIRECT_STEP,
      response
    };
  } catch (e) {
    throw { errors: backendErrors(e.response.data.fields) };
  }
};

export const redirectDashboard = (
  autoLoginUrl: string,
  is_new_dashboard = false
) => {
  const currentUrl = new URL(document.location.href);
  const loginUrl = new URL(autoLoginUrl);
  const returnUrl = currentUrl.searchParams.get("returnUrl");

  let redirectUrl = `${CONFIG.dashboardURL}${loginUrl.search}`;

  if (returnUrl) {
    // secure: remove hostname from redirect url
    const safeReturnUrl = returnUrl.replace(/^.*\/\/[^\/]+/, "");
    redirectUrl = `${safeReturnUrl}${
      safeReturnUrl.indexOf("?") === -1 ? "?" : "&"
    }${loginUrl.search.substr(1)}`;
  }

  // if (is_new_dashboard) {
  //   redirectUrl = redirectUrl.replace("/dashboard", "/dashboardvue3/");
  // }

  Cookie.set("last_login", new Date().toISOString(), { expires: 365 });

  window.location.assign(redirectUrl);
};
